import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Select,
  MenuItem,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Box,
  IconButton,
  LinearProgress,
} from "@mui/material";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";

export default function UploadQueryFilesDialog({
  openDialog,
  handleCloseDialog,
  arrDocumentCategory = [],
  handleFileUpload,
}) {
  const fileInputRef = useRef(null);

  const [tabs, setTabs] = useState([{ category: "Title deed", files: [] }]);
  const [selectedTabIndex, setSelectedTabIndex] = useState(null);
  const [loading, setLoading] = useState(false);

  // ✅ Reset when dialog opens
  useEffect(() => {
    if (openDialog) {
      setTabs([{ category: "Title deed", files: [] }]);
      setSelectedTabIndex(null);
      setLoading(false);
    }
  }, [openDialog]);

  // ✅ File selection
  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);

    const pdfFiles = selectedFiles.filter(
      (file) => file.type === "application/pdf",
    );

    if (selectedTabIndex === null) return;

    setTabs((prev) => {
      const updated = [...prev];

      const existingFiles = updated[selectedTabIndex].files;

      // ✅ Prevent duplicate files
      const newFiles = pdfFiles.filter(
        (newFile) =>
          !existingFiles.some(
            (f) => f.name === newFile.name && f.size === newFile.size,
          ),
      );

      updated[selectedTabIndex].files = [...existingFiles, ...newFiles];

      return updated;
    });

    event.target.value = ""; // reset input
  };

  // ✅ Remove file
  const removeFile = (tabIndex, fileIndex) => {
    setTabs((prev) => {
      const updated = [...prev];
      updated[tabIndex].files = updated[tabIndex].files.filter(
        (_, i) => i !== fileIndex,
      );
      return updated;
    });
  };

  // ✅ Change category
  const handleCategoryChange = (tabIndex, value) => {
    setTabs((prev) => {
      const updated = [...prev];
      updated[tabIndex].category = value;
      return updated;
    });
  };

  // ✅ Add category
  const handleAddTab = () => {
    setTabs((prev) => [...prev, { category: "Title deed", files: [] }]);
  };

  // ✅ Delete category
  const handleDeleteTab = (tabIndex) => {
    setTabs((prev) => prev.filter((_, i) => i !== tabIndex));

    if (selectedTabIndex === tabIndex) {
      setSelectedTabIndex(null);
    }
  };

  // ✅ Upload
  const handleUpload = async () => {
    try {
      setLoading(true);

      const validTabs = tabs.filter((tab) => tab.files.length > 0);

      await handleFileUpload(validTabs);

      handleCloseDialog();
      setTabs([{ category: "Title deed", files: [] }]);
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={openDialog}
      onClose={!loading ? handleCloseDialog : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, p: 3, minHeight: "400px" },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          fontWeight: 600,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Upload Query Files
        <Button
          size="small"
          variant="contained"
          onClick={handleAddTab}
          disabled={loading}
        >
          + Add Category
        </Button>
      </DialogTitle>

      {/* Progress */}
      {loading && (
        <Box sx={{ px: 2, pb: 1 }}>
          <LinearProgress />
          <Typography variant="caption">Uploading files...</Typography>
        </Box>
      )}

      <DialogContent dividers>
        {tabs.map((tab, tabIndex) => (
          <Box
            key={tabIndex}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              p: 2,
              mb: 2,
              backgroundColor: "#fafafa",
            }}
          >
            {/* Title + Delete */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>
                {tab.category || `Category ${tabIndex + 1}`}
              </Typography>

              {tabs.length > 1 && (
                <IconButton
                  color="error"
                  size="small"
                  disabled={loading}
                  onClick={() => handleDeleteTab(tabIndex)}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>

            {/* Category + Upload */}
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={tab.category}
                    label="Category"
                    disabled={loading}
                    onChange={(e) =>
                      handleCategoryChange(tabIndex, e.target.value)
                    }
                  >
                    {arrDocumentCategory.map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<UploadFileIcon />}
                  disabled={loading}
                  onClick={() => {
                    setSelectedTabIndex(tabIndex);
                    fileInputRef.current?.click();
                  }}
                >
                  Select Files
                </Button>
              </Grid>
            </Grid>

            {/* Files */}
            {tab.files.length > 0 && (
              <Box mt={2}>
                {tab.files.map((file, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      p: 1,
                      mb: 1,
                      backgroundColor: "#fff",
                    }}
                  >
                    <Typography variant="body2">{file.name}</Typography>

                    <IconButton
                      color="error"
                      size="small"
                      disabled={loading}
                      onClick={() => removeFile(tabIndex, index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ))}

        {/* Hidden input */}
        <input
          hidden
          multiple
          type="file"
          accept="application/pdf"
          ref={fileInputRef}
          onChange={handleFileChange}
        />
      </DialogContent>

      <DialogActions sx={{ mt: 2 }}>
        <Button
          variant="contained"
          onClick={handleCloseDialog}
          disabled={loading}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          disabled={loading || tabs.every((tab) => tab.files.length === 0)}
          onClick={handleUpload}
        >
          {loading ? "Uploading..." : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
